/**
 * =============================================================================
 * Componente: ReferralQuotaInline
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Indicador inline (badge + tooltip) do uso/limite de campanhas, programas
 *   ou afiliados do módulo Indicação. Quando a quota é atingida, mostra um
 *   CTA "Fazer upgrade" que abre o paywall em modal.
 * =============================================================================
 */
import { ReactNode, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useReferralAccess, hasQuotaAvailable } from '@/hooks/useReferralAccess';
import { ReferralPaywall } from './ReferralPaywall';
import { Lock } from 'lucide-react';

interface ReferralQuotaInlineProps {
  resource: 'campaigns' | 'programs' | 'affiliates';
  /** Renderiza filhos (ex: botão "Novo X"). Quando sem quota, intercepta o click e abre paywall */
  children?: ReactNode;
  className?: string;
}

const labelMap: Record<ReferralQuotaInlineProps['resource'], string> = {
  campaigns: 'campanhas',
  programs: 'programas',
  affiliates: 'afiliados',
};

export function ReferralQuotaInline({ resource, children, className }: ReferralQuotaInlineProps) {
  const { data } = useReferralAccess();
  const [paywallOpen, setPaywallOpen] = useState(false);

  if (!data) return <>{children}</>;

  const limit = data.limits[resource];
  const used = data.usage[resource];
  const available = hasQuotaAvailable(data, resource);
  const isUnlimited = limit === null || limit === undefined;

  // Badge de quota removido — informação já exibida pelo ReferralUsageInline acima (evita redundância visual)

  // Quando o filho é fornecido e não há quota, interceptamos o click via wrapper
  const wrappedChildren =
    children && !available ? (
      <span
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setPaywallOpen(true);
        }}
        // Bloqueia interação no botão real subjacente
        className="relative inline-flex cursor-not-allowed [&>*]:pointer-events-none [&>*]:opacity-60"
      >
        {children}
      </span>
    ) : (
      children
    );

  return (
    <>
      <div className={`inline-flex items-center gap-2 ${className || ''}`}>
        {wrappedChildren}
      </div>

      <Dialog open={paywallOpen} onOpenChange={setPaywallOpen}>
        <DialogContent className="max-w-5xl p-0">
          <ReferralPaywall
            title={`Limite de ${labelMap[resource]} atingido`}
            subtitle={`Seu plano atual permite ${limit} ${labelMap[resource]}. Faça upgrade para criar mais.`}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
